import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiSearch, FiShoppingCart, FiTrash2, FiPlus, FiX,
  FiUser, FiPhone, FiMail, FiMapPin, FiPackage, FiCheck,
  FiImage, FiCreditCard, FiDollarSign, FiShield, FiArrowUpRight,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { db } from "./firebase";
import emailjs from "emailjs-com";
import {
  collection, addDoc, deleteDoc, doc, onSnapshot,
  serverTimestamp, getDocs, query, where,
} from "firebase/firestore";
import QRCode from "react-qr-code";
import Swal from "sweetalert2";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css";     // design system: tokens, .kk-btn, .kk-eyebrow
import "./Products.css"; // everything unique to this page

/* ═══════════════ DATA (presentational only — logic untouched) ═══════════════ */

const ADMIN_EMAIL = "karanksxxx@gmail.com";
const FALLBACK_IMG = "/img/proj-products.jpg";
const SWAL = { background: "#0a1330", color: "#e9efff" };

// Demo items for preview when Firebase is not configured.
// (In your project with real `db`, these are never used — onSnapshot loads Firestore.)
const DEMO_PRODUCTS = [
  { id: "demo-1", name: "Java Master Notes", price: 99, image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=600&auto=format&fit=crop", fb: "/img/proj-notes.jpg" },
  { id: "demo-2", name: "React Starter Kit", price: 149, image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=600&auto=format&fit=crop", fb: "/img/proj-darkhackers.jpg" },
  { id: "demo-3", name: "DSA Handbook", price: 79, image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=600&auto=format&fit=crop", fb: "/img/proj-aushlink.jpg" },
  { id: "demo-4", name: "Portfolio Template", price: 199, image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?q=80&w=600&auto=format&fit=crop", fb: "/img/proj-realtycore.jpg" },
  { id: "demo-5", name: "Web Dev Bundle", price: 249, image: "https://images.unsplash.com/photo-1547658719-da2b51169166?q=80&w=600&auto=format&fit=crop", fb: "/img/proj-listing.jpg" },
  { id: "demo-6", name: "UI Design Pack", price: 129, image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?q=80&w=600&auto=format&fit=crop", fb: "/img/proj-products.jpg" },
];

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

/* ═══════════════ PARTICLES ═══════════════ */

function ProductParticles() {
  const parts = useRef(
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="pd-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="pd-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.06, 0.34, 0.06] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* ═══════════════ PRODUCT CARD ═══════════════ */

function ProductCard({ product, isAdmin, onBuy, onDelete, onImageClick, index }) {
  const [broken, setBroken] = useState(false);
  const src = broken ? (product.fb || FALLBACK_IMG) : product.image;

  return (
    <motion.article
      className="pd-card"
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ delay: (index % 3) * 0.07, duration: 0.5 }}
      whileHover={{ y: -5 }}
    >
      <button
        type="button"
        className="pd-media"
        onClick={() => onImageClick(product.image)}
        aria-label={`View ${product.name}`}
      >
        <img
          src={src}
          alt={product.name}
          className="pd-media__img"
          loading="lazy"
          onError={() => setBroken(true)}
        />
        <span className="pd-media__veil" aria-hidden="true" />
        <span className="pd-media__zoom"><FiImage /> View</span>
        <span className="pd-media__price">₹{product.price}</span>
      </button>

      <div className="pd-card__body">
        <span className="pd-card__tag"><FiPackage /> Digital</span>
        <h3 className="pd-card__title">{product.name}</h3>

        <div className="pd-card__foot">
          <span className="pd-price"><i>₹</i>{product.price}</span>

          {!isAdmin ? (
            <motion.button
              className="kk-btn pd-buy"
              onClick={() => onBuy(product)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.985 }}
            >
              <FiShoppingCart /> Buy now
            </motion.button>
          ) : (
            <motion.button
              className="kk-btn kk-btn--ghost pd-del"
              onClick={() => onDelete(product.id)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.985 }}
            >
              <FiTrash2 /> Delete
            </motion.button>
          )}
        </div>
      </div>
    </motion.article>
  );
}

/* ═══════════════ ADMIN FORM ═══════════════ */

function AdminForm({ name, price, image, onNameChange, onPriceChange, onImageChange, onSubmit }) {
  return (
    <motion.section
      className="pd-admin"
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55 }}
    >
      <div className="pd-admin__head">
        <div>
          <span className="kk-eyebrow">Owner / New listing</span>
          <h2 className="pd-h2">Add a <span className="kk-serif kk-acid">product</span></h2>
        </div>
        <span className="pd-admin__badge"><FiShield /> Admin only</span>
      </div>

      <div className="pd-admin__grid">
        <div className="pd-field">
          <label htmlFor="pd-name">Product name</label>
          <div className="pd-input">
            <FiPackage className="pd-input__ic" />
            <input
              id="pd-name"
              type="text"
              placeholder="Product name"
              value={name}
              onChange={onNameChange}
            />
          </div>
        </div>

        <div className="pd-field">
          <label htmlFor="pd-price">Price (₹)</label>
          <div className="pd-input">
            <FiDollarSign className="pd-input__ic" />
            <input
              id="pd-price"
              type="number"
              placeholder="Price"
              value={price}
              onChange={onPriceChange}
            />
          </div>
        </div>

        <div className="pd-field pd-field--file">
          <label htmlFor="product-image">Product image</label>
          <div className="pd-file">
            <input
              type="file"
              accept="image/*"
              onChange={onImageChange}
              id="product-image"
              className="pd-file__input"
            />
            <label htmlFor="product-image" className="kk-btn kk-btn--ghost pd-file__btn">
              <FiImage /> Choose image
            </label>
            {image && <span className="pd-file__ok"><FiCheck /> Ready</span>}
          </div>
        </div>
      </div>

      {image && (
        <div className="pd-admin__preview">
          <img src={image} alt="Preview" />
          <span><FiCheck /> Preview</span>
        </div>
      )}

      <motion.button
        className="kk-btn pd-admin__submit"
        onClick={onSubmit}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.985 }}
      >
        <FiPlus /> Add product
      </motion.button>
    </motion.section>
  );
}

/* ═══════════════ BUYER FORM MODAL ═══════════════ */

function BuyerFormModal({ product, buyer, onBuyerChange, onPlaceOrder, onClose }) {
  return (
    <motion.div
      className="pd-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="pd-modal"
        initial={{ scale: 0.94, y: 28, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, y: 28, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Delivery details"
      >
        <div className="pd-modal__head">
          <div className="pd-modal__title">
            <span className="kk-eyebrow">Checkout / Step 1</span>
            <h2 className="pd-h2 pd-h2--sm">Delivery <span className="kk-serif">details</span></h2>
            <span className="pd-modal__meta"><FiPackage /> Ordering: {product.name}</span>
          </div>
          <motion.button className="pd-modal__x" onClick={onClose} whileHover={{ rotate: 90 }} aria-label="Close">
            <FiX />
          </motion.button>
        </div>

        <div className="pd-modal__body">
          <div className="pd-modal__grid">
            <div className="pd-field">
              <label htmlFor="pd-bname"><FiUser /> Full name</label>
              <div className="pd-input">
                <FiUser className="pd-input__ic" />
                <input
                  id="pd-bname"
                  type="text"
                  placeholder="Your name"
                  value={buyer.name}
                  onChange={(e) => onBuyerChange({ ...buyer, name: e.target.value })}
                />
              </div>
            </div>

            <div className="pd-field">
              <label htmlFor="pd-bcontact"><FiPhone /> Contact</label>
              <div className="pd-input">
                <FiPhone className="pd-input__ic" />
                <input
                  id="pd-bcontact"
                  type="tel"
                  placeholder="Phone number"
                  value={buyer.contact}
                  onChange={(e) => onBuyerChange({ ...buyer, contact: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="pd-field">
            <label htmlFor="pd-bemail"><FiMail /> Email</label>
            <div className="pd-input pd-input--ro">
              <FiMail className="pd-input__ic" />
              <input id="pd-bemail" type="email" value={buyer.email} readOnly />
            </div>
          </div>

          <div className="pd-field">
            <label htmlFor="pd-baddr"><FiMapPin /> Address</label>
            <div className="pd-input pd-input--area">
              <FiMapPin className="pd-input__ic" />
              <textarea
                id="pd-baddr"
                placeholder="Full delivery address"
                value={buyer.address}
                onChange={(e) => onBuyerChange({ ...buyer, address: e.target.value })}
                rows="3"
              />
            </div>
          </div>

          <div className="pd-field">
            <label htmlFor="pd-bcity"><FiMapPin /> City</label>
            <div className="pd-input pd-input--ro">
              <FiMapPin className="pd-input__ic" />
              <input id="pd-bcity" type="text" value="Bathinda" readOnly />
            </div>
            <span className="pd-field__hint">Currently delivering in Bathinda only</span>
          </div>

          <div className="pd-sum">
            <div className="pd-sum__row">
              <span>Product</span>
              <b>{product.name}</b>
            </div>
            <div className="pd-sum__row pd-sum__row--total">
              <span>Total</span>
              <b>₹{product.price}</b>
            </div>
          </div>

          <div className="pd-pay">
            <motion.button
              className="kk-btn kk-btn--ghost pd-pay__btn"
              onClick={() => onPlaceOrder("COD")}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.985 }}
            >
              <FiDollarSign /> Cash on delivery
            </motion.button>
            <motion.button
              className="kk-btn pd-pay__btn"
              onClick={() => onPlaceOrder("UPI")}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.985 }}
            >
              <FiCreditCard /> Pay with UPI
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ QR PAYMENT MODAL ═══════════════ */

function QRPaymentModal({ product, onConfirmPaid, onClose }) {
  const upiLink = `upi://pay?pa=karanksxxx-1@oksbi&pn=Karan%20Kumar&am=${product.price}&cu=INR`;

  return (
    <motion.div
      className="pd-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="pd-modal pd-modal--qr"
        initial={{ scale: 0.94, y: 28, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, y: 28, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="UPI payment"
      >
        <div className="pd-modal__head">
          <div className="pd-modal__title">
            <span className="kk-eyebrow kk-eyebrow--violet">Checkout / Step 2</span>
            <h2 className="pd-h2 pd-h2--sm">UPI <span className="kk-serif">payment</span></h2>
            <span className="pd-modal__meta">Scan to pay ₹{product.price}</span>
          </div>
          <motion.button className="pd-modal__x" onClick={onClose} whileHover={{ rotate: 90 }} aria-label="Close">
            <FiX />
          </motion.button>
        </div>

        <div className="pd-modal__body pd-qr">
          <div className="pd-qr__box">
            <QRCode value={upiLink} size={190} bgColor="#0a1330" fgColor="#e9efff" level="H" />
          </div>

          <p className="pd-qr__amt">₹{product.price}</p>
          <p className="pd-qr__merchant">Karan Kumar</p>
          <p className="pd-qr__upi">karanksxxx-1@oksbi</p>

          <motion.button
            className="kk-btn pd-qr__btn"
            onClick={onConfirmPaid}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.985 }}
          >
            <FiCheck /> I have paid
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ IMAGE LIGHTBOX ═══════════════ */

function ImagePreviewModal({ imageUrl, onClose }) {
  if (!imageUrl) return null;

  return (
    <motion.div
      className="pd-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="pd-lightbox__box"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <motion.button
          className="pd-lightbox__x"
          onClick={onClose}
          whileHover={{ rotate: 90, scale: 1.08 }}
          aria-label="Close preview"
        >
          <FiX />
        </motion.button>
        <img src={imageUrl} alt="Preview" className="pd-lightbox__img" />
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ MAIN ═══════════════ */

// LOGIC: identical to your original — same state, same handlers, same
// Firebase calls. Only styling/classes + preview fallback added.
export default function Products() {
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [image, setImage] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [buyer, setBuyer] = useState({
    name: "",
    contact: "",
    email: "",
    address: "",
    city: "Bathinda",
  });

  const rootRef = useRef(null);
  useParallax(rootRef);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  // Preview-safe ref: identical to collection(db, "Products") when db exists.
  const productsRef = useMemo(() => {
    try {
      return db ? collection(db, "Products") : null;
    } catch {
      return null;
    }
  }, []);
  const isAdmin = user?.email === ADMIN_EMAIL;

  // Login check
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      const parsed = JSON.parse(stored);
      const email = parsed.email || parsed.Email || "";
      setUser({ email });
      setBuyer((p) => ({ ...p, email }));
      fetchUserDetails(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load products
  useEffect(() => {
    if (!productsRef) {
      const t = setTimeout(() => setProducts(DEMO_PRODUCTS), 500);
      return () => clearTimeout(t);
    }
    const unsub = onSnapshot(productsRef, (snap) => {
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [productsRef]);

  const fetchUserDetails = async (email) => {
    if (!db) return;
    const q = query(collection(db, "Users"), where("email", "==", email));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const d = snap.docs[0].data();
      setBuyer((p) => ({
        ...p,
        name: d.name || "",
        contact: d.contact || "",
        address: d.address || "",
        city: "Bathinda",
      }));
    }
  };

  // Send order email
  const sendOrderEmail = async ({ product, buyer, amount, paymentMethod, status }) => {
    if (!db) return;
    try {
      await emailjs.send(
        "service_nn606kn",
        "template_8i8t5yc",
        {
          product_name: product.name,
          amount,
          payment_mode: paymentMethod,
          status,
          buyer_name: buyer.name,
          buyer_email: buyer.email,
          buyer_contact: buyer.contact,
          buyer_city: buyer.city,
          buyer_address: buyer.address,
          order_time: new Date().toLocaleString(),
        },
        "CVy20pZ0JQ_XxmyCq"
      );
    } catch (err) {
      console.error("Email error", err);
    }
  };

  // Admin: Add product
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const addProduct = async () => {
    if (!name || !price || !image) {
      Swal.fire({ title: "Error", text: "All fields required", icon: "error", ...SWAL });
      return;
    }
    if (!productsRef) {
      setProducts((p) => [{ id: "local-" + Date.now(), name, price: Number(price), image }, ...p]);
      Swal.fire({ title: "Added", text: "Product added successfully", icon: "success", ...SWAL });
      setName("");
      setPrice("");
      setImage(null);
      return;
    }
    await addDoc(productsRef, {
      name,
      price: Number(price),
      image,
      createdAt: serverTimestamp(),
    });
    Swal.fire({ title: "Added", text: "Product added successfully", icon: "success", ...SWAL });
    setName("");
    setPrice("");
    setImage(null);
  };

  const deleteProduct = async (id) => {
    const result = await Swal.fire({
      title: "Delete Product?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f43f5e",
      cancelButtonColor: "#3b4a6b",
      confirmButtonText: "Delete",
      ...SWAL,
    });
    if (result.isConfirmed) {
      if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
        setProducts((p) => p.filter((pr) => pr.id !== id));
        Swal.fire({ title: "Deleted", text: "Product removed", icon: "success", ...SWAL });
        return;
      }
      await deleteDoc(doc(db, "Products", id));
      Swal.fire({ title: "Deleted", text: "Product removed", icon: "success", ...SWAL });
    }
  };

  // Buy flow
  const startBuy = (product) => {
    if (!user?.email) {
      Swal.fire({
        title: "Login Required",
        text: "Please login first",
        icon: "warning",
        ...SWAL,
      });
      return;
    }
    setSelectedProduct(product);
    setShowForm(true);
    setShowQR(false);
  };

  const validateBuyer = () =>
    buyer.name && buyer.contact && buyer.address && buyer.city === "Bathinda";

  const placeOrder = async (method) => {
    if (!validateBuyer()) {
      Swal.fire({
        title: "Error",
        text: "Fill all details (City must be Bathinda)",
        icon: "error",
        ...SWAL,
      });
      return;
    }

    if (db) {
      await addDoc(collection(db, "Orders"), {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        amount: selectedProduct.price,
        buyer,
        paymentMethod: method,
        status: method === "COD" ? "CONFIRMED" : "PAYMENT_PENDING",
        createdAt: serverTimestamp(),
      });

      await sendOrderEmail({
        product: selectedProduct,
        buyer,
        amount: selectedProduct.price,
        paymentMethod: method,
        status: method === "COD" ? "CONFIRMED" : "PAYMENT_PENDING",
      });
    }

    if (method === "COD") {
      Swal.fire({
        title: "Order Placed 🎉",
        text: "Cash on Delivery selected",
        icon: "success",
        ...SWAL,
      });
      setShowForm(false);
      setSelectedProduct(null);
    } else {
      setShowQR(true);
    }
  };

  const confirmPaid = async () => {
    await sendOrderEmail({
      product: selectedProduct,
      buyer,
      amount: selectedProduct.price,
      paymentMethod: "UPI",
      status: "PAYMENT_DONE",
    });
    Swal.fire({
      title: "Payment Submitted",
      text: "Admin will verify",
      icon: "success",
      ...SWAL,
    });
    setShowQR(false);
    setSelectedProduct(null);
  };

  const filtered = products.filter((p) =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="kk-root pd-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <ProductParticles />

      {/* ══════ HERO ══════ */}
      <header className="pd-hero">
        <div className="pd-hero__bg" aria-hidden="true">
          <span className="pd-hero__grid" data-plx="0.05" />
          <span className="pd-hero__glow pd-hero__glow--acid" data-plx="0.14" />
          <span className="pd-hero__glow pd-hero__glow--violet" data-plx="0.22" />
          <span className="pd-hero__word" data-plx="0.08">STORE</span>
        </div>

        <div className="kk-wrap pd-hero__in">
          <motion.div
            className="pd-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="pd-kicker">
              <span className="kk-dot" />
              <span>Shop</span>
              <span className="kk-slash">/</span>
              <span>Store</span>
            </div>

            <h1 className="pd-title">
              My <span className="kk-serif kk-acid">products.</span>
              <span className="pd-title__sub">notes, kits &amp; templates.</span>
            </h1>

            <p className="pd-lead">
              Small, useful things I actually use — built from the same notes and code
              that ship my client work. Checkout with UPI or cash on delivery.
            </p>

            <div className="pd-meta">
              <div><b>{String(filtered.length).padStart(2, "0")}</b><span>Products</span></div>
              <div><b>₹79</b><span>Starting at</span></div>
              <div><b>2</b><span>Payment modes</span></div>
            </div>
          </motion.div>

          {/* search + count */}
          <motion.div
            className="pd-tools"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.14 }}
          >
            <label className="pd-search" htmlFor="pd-search">
              <FiSearch className="pd-search__ic" />
              <input
                id="pd-search"
                type="text"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <motion.button
                  className="pd-search__x"
                  onClick={() => setSearch("")}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  aria-label="Clear search"
                >
                  <FiX />
                </motion.button>
              )}
            </label>

            <div className="pd-pill"><FiPackage /> {filtered.length} products</div>
            {isAdmin && <div className="pd-pill pd-pill--admin"><FiShield /> Admin</div>}
          </motion.div>
        </div>
      </header>

      {/* ══════ MAIN ══════ */}
      <main className="pd-main">
        <div className="kk-wrap">
          {isAdmin && (
            <AdminForm
              name={name}
              price={price}
              image={image}
              onNameChange={(e) => setName(e.target.value)}
              onPriceChange={(e) => setPrice(e.target.value)}
              onImageChange={handleImage}
              onSubmit={addProduct}
            />
          )}

          <div className="pd-listhead">
            <div>
              <span className="kk-eyebrow">01 — Catalogue</span>
              <h2 className="pd-h2">Available <span className="kk-serif">products</span></h2>
            </div>
            <p className="pd-listhead__aside">
              Every purchase is delivered digitally. Cash on delivery is available
              inside Bathinda; UPI works everywhere.
            </p>
          </div>

          {filtered.length === 0 ? (
            <motion.div className="pd-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="pd-empty__ic"><FiPackage /></div>
              <h3>No products found</h3>
              <p>
                {search ? (
                  <>No results for <strong>“{search}”</strong>. Try a shorter word.</>
                ) : (
                  "Products will appear here once added."
                )}
              </p>
              {search && (
                <button className="kk-btn kk-btn--ghost pd-empty__btn" onClick={() => setSearch("")}>
                  Clear search
                </button>
              )}
            </motion.div>
          ) : (
            <div className="pd-grid">
              {filtered.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  isAdmin={isAdmin}
                  onBuy={startBuy}
                  onDelete={deleteProduct}
                  onImageClick={setPreviewImg}
                />
              ))}
            </div>
          )}

          <div className="pd-note">
            <span><FiCheck /> Instant digital delivery</span>
            <span><FiCheck /> UPI &amp; cash on delivery</span>
            <span><FiCheck /> Support on WhatsApp</span>
          </div>
        </div>
      </main>

      {/* ══════ MODALS ══════ */}
      <AnimatePresence>
        {showForm && selectedProduct && (
          <BuyerFormModal
            product={selectedProduct}
            buyer={buyer}
            onBuyerChange={setBuyer}
            onPlaceOrder={placeOrder}
            onClose={() => { setShowForm(false); setSelectedProduct(null); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showQR && selectedProduct && (
          <QRPaymentModal
            product={selectedProduct}
            onConfirmPaid={confirmPaid}
            onClose={() => { setShowQR(false); setSelectedProduct(null); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {previewImg && (
          <ImagePreviewModal
            imageUrl={previewImg}
            onClose={() => setPreviewImg(null)}
          />
        )}
      </AnimatePresence>

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      <Navbar />
      <Foot />
    </div>
  );
}
